import { gsap } from "gsap";
import { Mail } from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { MOTION_DURATION, MOTION_EASE, MOTION_STAGGER } from "@/shared/components/motion/motion.tokens";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { supportsEmailBackup } from "@/shared/utils/stepUp";

import { AuthenticatorIllustration } from "./AuthenticatorIllustration";
import { MethodIllustrationStage } from "./MethodIllustrationStage";
import { SecurityPinIllustration } from "./SecurityPinIllustration";

const LEVELS = 3;

/**
 * Three pips for one quality. The two methods trade the same two qualities against each other, so the pips
 * say in one glance what the copy says in a sentence: the app is safer, the PIN is quicker.
 */
function Meter({ label, value, tone }: { label: string; value: number; tone: "success" | "primary" }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11.5px] text-muted-foreground">{label}</span>
      <span className="flex gap-1" role="img" aria-label={`${label}: ${value}/${LEVELS}`}>
        {Array.from({ length: LEVELS }).map((_, i) => (
          <span key={i} className="relative h-1.5 w-5 overflow-hidden rounded-full bg-muted">
            {i < value ? (
              <span
                data-meter-fill=""
                className={cn(
                  "absolute inset-0 rounded-full",
                  tone === "success" ? "bg-success" : "bg-primary",
                )}
              />
            ) : null}
          </span>
        ))}
      </span>
    </div>
  );
}

interface MethodColumnProps {
  illustration: ReactNode;
  name: string;
  badge: string;
  badgeVariant: "success" | "primary";
  body: string;
  security: number;
  speed: number;
  time: string;
  /** How the email backup comes with this method: included with the PIN, optional with the app. */
  emailNote?: string;
  delayClass: string;
  emphasis?: boolean;
}

function MethodColumn({
  illustration,
  name,
  badge,
  badgeVariant,
  body,
  security,
  speed,
  time,
  emailNote,
  delayClass,
  emphasis,
}: MethodColumnProps) {
  const { t } = useTranslations("global");
  return (
    <div
      className={cn(
        "flex animate-fade-up flex-col gap-3 rounded-lg border p-4 animate-duration-300 motion-reduce:animate-none",
        delayClass,
        emphasis ? "border-success/40 bg-success/5" : "border-border bg-background",
      )}
    >
      <MethodIllustrationStage>{illustration}</MethodIllustrationStage>
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-foreground">{name}</div>
        <ColocaBadge variant={badgeVariant} className="mt-1">
          {badge}
        </ColocaBadge>
      </div>
      <p className="text-[12.5px] leading-relaxed text-muted-foreground">{body}</p>
      {emailNote ? (
        <p className="-mt-1 flex items-center gap-1.5 text-[11.5px] font-medium text-foreground/80">
          <Mail size={12} className="shrink-0 text-primary" aria-hidden="true" />
          {emailNote}
        </p>
      ) : null}
      <div className="mt-auto flex flex-col gap-1.5 border-t border-dashed pt-3">
        <Meter label={t("step_up_meter_security")} value={security} tone="success" />
        <Meter label={t("step_up_meter_speed")} value={speed} tone="primary" />
        <div className="pt-1 text-[11.5px] font-medium text-foreground">{time}</div>
      </div>
    </div>
  );
}

/**
 * The authenticator app next to the security PIN, honestly: the app is recommended because it is the more
 * secure of the two, the PIN is the quicker one to set up. Used where a member has neither yet. Each says how
 * the email backup comes with it — on with the PIN, the person's call with the app.
 */
export function StepUpMethodComparison({ className }: { className?: string }) {
  const { t } = useTranslations("global");
  const { user } = useAuth();
  const withEmail = supportsEmailBackup(user);
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  // The pips fill left to right once the cards have landed, so the trade-off reads as it is drawn: the app's
  // security bar fills all the way, the PIN's speed bar does. GSAP owns the from-state, so without motion the
  // pips are simply full.
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || reduced) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-meter-fill]",
        { scaleX: 0, transformOrigin: "left center" },
        {
          scaleX: 1,
          duration: MOTION_DURATION.quick * 1.6,
          ease: MOTION_EASE.enter,
          stagger: MOTION_STAGGER.base,
          delay: 0.35,
        },
      );
    }, root);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <div ref={ref} className={cn("flex flex-col gap-3", className)}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MethodColumn
          illustration={<AuthenticatorIllustration />}
          name={t("step_up_choice_totp_name")}
          badge={t("step_up_choice_totp_badge")}
          badgeVariant="success"
          body={t("step_up_choice_totp_body")}
          security={3}
          speed={1}
          time={t("step_up_choice_totp_time")}
          emailNote={withEmail ? t("step_up_choice_totp_email_note") : undefined}
          delayClass="animate-delay-0"
          emphasis
        />
        <MethodColumn
          illustration={<SecurityPinIllustration />}
          name={t("step_up_choice_pin_name")}
          badge={t("step_up_choice_pin_badge")}
          badgeVariant="primary"
          body={t("step_up_choice_pin_body")}
          security={2}
          speed={3}
          time={t("step_up_choice_pin_time")}
          emailNote={withEmail ? t("step_up_choice_pin_email_note") : undefined}
          delayClass="animate-delay-75"
        />
      </div>
      <p className="animate-fade text-[12px] text-muted-foreground animate-delay-150 motion-reduce:animate-none">
        {t("step_up_choice_footnote")}
      </p>
    </div>
  );
}
